/**
 * One item, in full: what the checklist says about it, the controls that change its state,
 * and the record of who changed what.
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import Button from '../../../components/Button';
import { Modal } from '../../../components/Modal';
import { hipaaService } from '../services/hipaaService';
import {
  EFFORT_LABELS,
  HIPAA_EFFORTS,
  HIPAA_STATUSES,
  KIND_LABELS,
  STATUS_LABELS,
  type HipaaActivityEntry,
  type HipaaComment,
  type HipaaEffort,
  type HipaaItem,
  type HipaaItemPatch,
  type HipaaStatus,
} from '../types/hipaaTypes';
import { BlockedBadge, PriorityBadge, SpecBadge, StatusPill, VerificationBadge } from './Badges';

interface ItemDetailModalProps {
  item: HipaaItem;
  /** Shown instead of `item.status` for a crosswalk row that is following its sections. */
  displayStatus: HipaaStatus;
  onClose: () => void;
  onUpdate: (itemId: string, patch: HipaaItemPatch) => Promise<HipaaItem>;
  onAddComment: (itemId: string, text: string) => Promise<HipaaComment>;
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{label}</p>
    {children}
  </div>
);

const formatWhen = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';

const describeChange = (entry: HipaaActivityEntry): string => {
  switch (entry.type) {
    case 'status':
      return `Status: ${STATUS_LABELS[entry.from as HipaaStatus] ?? entry.from} → ${
        STATUS_LABELS[entry.to as HipaaStatus] ?? entry.to
      }`;
    case 'effort':
      return `Effort: ${entry.from ?? 'unset'} → ${entry.to ?? 'unset'}`;
    case 'blocked':
      return entry.to === 'true' ? `Blocked — ${entry.text ?? 'no reason given'}` : 'Unblocked';
    case 'comment':
      return 'Commented';
  }
};

export const ItemDetailModal = ({
  item,
  displayStatus,
  onClose,
  onUpdate,
  onAddComment,
}: ItemDetailModalProps) => {
  const [comments, setComments] = useState<HipaaComment[]>([]);
  const [history, setHistory] = useState<HipaaActivityEntry[]>([]);
  const [detailLoading, setDetailLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [draftComment, setDraftComment] = useState('');
  const [draftReason, setDraftReason] = useState(item.blockedReason);

  const isDerived = item.kind === 'crosswalk' && !item.statusIsExplicit;

  const loadDetail = useCallback(async () => {
    setDetailLoading(true);
    try {
      const detail = await hipaaService.getItemDetail(item.id);
      setComments(detail.comments);
      setHistory(detail.activity);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to load item history.');
    } finally {
      setDetailLoading(false);
    }
  }, [item.id]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  // The blocked reason is a free-text draft, so resync it when a different item is opened.
  useEffect(() => {
    setDraftReason(item.blockedReason);
  }, [item.id, item.blockedReason]);

  const applyPatch = async (patch: HipaaItemPatch) => {
    setSaving(true);
    setError(null);
    try {
      await onUpdate(item.id, patch);
      await loadDetail();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const submitComment = async () => {
    const text = draftComment.trim();
    if (!text) return;

    setSaving(true);
    setError(null);
    try {
      const comment = await onAddComment(item.id, text);
      setComments((previous) => [...previous, comment]);
      setDraftComment('');
      await loadDetail();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to add the comment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={item.title} size="2xl">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-mono text-gray-400">
            {KIND_LABELS[item.kind]} · §{item.section}
          </span>
          <StatusPill status={displayStatus} derived={isDerived} />
          <PriorityBadge priorities={item.priorities} />
          <SpecBadge specs={item.specs} />
          {item.blocked && <BlockedBadge reason={item.blockedReason} />}
          {item.needsVerification && <VerificationBadge />}
        </div>

        {item.citations.length > 0 && (
          <p className="text-xs text-gray-500">
            <span className="font-medium text-gray-600">45 CFR</span> {item.citations.join(' · ')}
          </p>
        )}

        {item.statusNote && (
          <Field label="Where we stand">
            <p className="text-sm text-gray-700 leading-relaxed">{item.statusNote}</p>
          </Field>
        )}

        {item.actions && (
          <Field label="What has to happen">
            <p className="text-sm text-gray-700 leading-relaxed">{item.actions}</p>
          </Field>
        )}

        {item.vendor && (
          <Field label="Vendor position">
            <dl className="text-sm text-gray-700 space-y-1">
              <div className="flex gap-2">
                <dt className="text-gray-500 w-24 sm:w-32 shrink-0">Health data</dt>
                <dd>{item.vendor.healthData}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-gray-500 w-24 sm:w-32 shrink-0">BAA</dt>
                <dd>{item.vendor.baaStatus}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-gray-500 w-24 sm:w-32 shrink-0">Disposition</dt>
                <dd>{item.vendor.disposition}</dd>
              </div>
            </dl>
          </Field>
        )}

        {item.mapsTo.length > 0 && (
          <Field label={item.kind === 'crosswalk' ? 'Follows sections' : 'Feeds sections'}>
            <p className="text-sm text-gray-700">
              {item.mapsTo.map((ref) => `§${ref}`).join(', ')}
            </p>
          </Field>
        )}

        <div className="border-t border-gray-100 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Status">
            <div className="flex flex-wrap gap-1.5">
              {HIPAA_STATUSES.map((status) => (
                <Button
                  key={status}
                  size="sm"
                  variant={item.status === status && !isDerived ? 'primary' : 'outline'}
                  disabled={saving}
                  onClick={() => applyPatch({ status })}
                >
                  {STATUS_LABELS[status]}
                </Button>
              ))}
            </div>
            {isDerived && (
              <p className="mt-2 text-xs text-gray-400">
                Following §{item.mapsTo.join(', §')}. Picking a status pins this row.
              </p>
            )}
            {item.kind === 'crosswalk' && item.statusIsExplicit && item.mapsTo.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="mt-2"
                disabled={saving}
                onClick={() => applyPatch({ statusIsExplicit: false })}
              >
                Follow sections again
              </Button>
            )}
          </Field>

          <Field label="Effort">
            <div className="flex flex-wrap gap-1.5">
              {HIPAA_EFFORTS.map((effort: HipaaEffort) => (
                <Button
                  key={effort}
                  size="sm"
                  variant={item.effort === effort ? 'primary' : 'outline'}
                  disabled={saving}
                  title={EFFORT_LABELS[effort]}
                  onClick={() => applyPatch({ effort: item.effort === effort ? null : effort })}
                >
                  {effort}
                </Button>
              ))}
            </div>
          </Field>
        </div>

        <Field label="Blocked">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={item.blocked ? 'danger' : 'outline'}
                disabled={saving}
                onClick={() =>
                  applyPatch({ blocked: !item.blocked, blockedReason: item.blocked ? '' : draftReason })
                }
              >
                {item.blocked ? 'Blocked' : 'Not blocked'}
              </Button>
              <span className="text-xs text-gray-400">
                {item.blocked ? 'Click to unblock' : 'Click to mark as blocked'}
              </span>
            </div>
            {item.blocked && (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={draftReason}
                  onChange={(event) => setDraftReason(event.target.value)}
                  placeholder="What is it waiting on?"
                  className="flex-1 min-w-0 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={saving || draftReason === item.blockedReason}
                  onClick={() => applyPatch({ blocked: true, blockedReason: draftReason })}
                >
                  Save
                </Button>
              </div>
            )}
          </div>
        </Field>

        {error && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </p>
        )}

        <div className="border-t border-gray-100 pt-4">
          <Field label={`Comments (${comments.length})`}>
            {detailLoading ? (
              <p className="flex items-center gap-2 text-xs text-gray-400 py-2">
                <Loader2 size={12} className="animate-spin" /> Loading…
              </p>
            ) : (
              <ul className="space-y-3 mb-3">
                {comments.length === 0 && (
                  <li className="text-xs text-gray-400">No comments yet.</li>
                )}
                {comments.map((comment) => (
                  <li key={comment.id} className="text-sm">
                    <p className="text-gray-700 whitespace-pre-wrap break-words">{comment.text}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {comment.author.email || 'unknown'} · {formatWhen(comment.createdAt)}
                    </p>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex gap-2">
              <textarea
                value={draftComment}
                onChange={(event) => setDraftComment(event.target.value)}
                placeholder="Add a note…"
                rows={2}
                className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y"
              />
              <Button
                size="sm"
                variant="primary"
                disabled={saving || !draftComment.trim()}
                icon={<Send size={13} />}
                onClick={submitComment}
              >
                Post
              </Button>
            </div>
          </Field>
        </div>

        <div className="border-t border-gray-100 pt-4">
          <Field label="History">
            {history.length === 0 ? (
              <p className="text-xs text-gray-400">Nothing recorded yet.</p>
            ) : (
              <ul className="space-y-2">
                {history.map((entry) => (
                  <li key={entry.id} className="text-xs flex gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0" />
                    <span>
                      <span className="text-gray-700">{describeChange(entry)}</span>
                      <span className="block text-[10px] text-gray-400">
                        {entry.actor.email || 'unknown'} · {formatWhen(entry.at)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Field>
        </div>
      </div>
    </Modal>
  );
};
